import React, { useState } from 'react';
import { AlertCircleIcon, ArrowLeftIcon, FileIcon, UploadIcon, XIcon } from 'lucide-react';
export function UploadInvoice({
  vendor,
  onSuccess
}) {
  const [invoiceData, setInvoiceData] = useState({
    poNumber: '',
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    amount: '',
    description: '',
    items: [{
      description: '',
      quantity: 1,
      unitPrice: 0
    }]
  });
  const [fileSelected, setFileSelected] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setInvoiceData({
      ...invoiceData,
      [name]: value
    });
    // Clear error when field is updated
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoiceData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    // Recalculate totals
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(updatedItems[index].quantity) || 0;
      const unitPrice = field === 'unitPrice' ? parseFloat(value) || 0 : parseFloat(updatedItems[index].unitPrice) || 0;
      updatedItems[index].total = quantity * unitPrice;
    }
    setInvoiceData({
      ...invoiceData,
      items: updatedItems
    });
    // Update total amount
    const total = updatedItems.reduce((sum, item) => sum + (item.total || 0), 0);
    setInvoiceData(prev => ({
      ...prev,
      amount: total.toFixed(2)
    }));
  };
  const addItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [...invoiceData.items, {
        description: '',
        quantity: 1,
        unitPrice: 0,
        total: 0
      }]
    });
  };
  const removeItem = index => {
    if (invoiceData.items.length === 1) return;
    const updatedItems = invoiceData.items.filter((_, i) => i !== index);
    setInvoiceData({
      ...invoiceData,
      items: updatedItems
    });
    // Update total amount
    const total = updatedItems.reduce((sum, item) => sum + (item.total || 0), 0);
    setInvoiceData(prev => ({
      ...prev,
      amount: total.toFixed(2)
    }));
  };
  const handleFileChange = e => {
    if (e.target.files && e.target.files[0]) {
      setFileSelected(e.target.files[0]);
      if (errors.file) {
        setErrors({
          ...errors,
          file: null
        });
      }
    }
  };
  const validateForm = () => {
    const newErrors = {};
    if (!invoiceData.invoiceNumber) newErrors.invoiceNumber = 'Invoice number is required';
    if (!invoiceData.invoiceDate) newErrors.invoiceDate = 'Invoice date is required';
    if (!invoiceData.amount) newErrors.amount = 'Amount is required';
    if (!fileSelected) newErrors.file = 'Please attach an invoice file';
    // Validate line items
    const itemErrors = [];
    invoiceData.items.forEach((item, index) => {
      const itemError = {};
      if (!item.description) itemError.description = 'Description is required';
      if (!item.quantity || item.quantity <= 0) itemError.quantity = 'Valid quantity is required';
      if (!item.unitPrice || item.unitPrice <= 0) itemError.unitPrice = 'Valid price is required';
      if (Object.keys(itemError).length > 0) {
        itemErrors[index] = itemError;
      }
    });
    if (itemErrors.length > 0) {
      newErrors.items = itemErrors;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      onSuccess();
    }, 1500);
  };
  return <div data-id="element-941">
      <div className="mb-6" data-id="element-942">
        <button onClick={() => onSuccess()} className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-900" data-id="element-943">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-944" />
          Back to Invoices
        </button>
      </div>
      <div className="mb-8" data-id="element-945">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-946">Upload New Invoice</h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-947">
          Fill in the details below and attach your invoice document
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-8" data-id="element-948">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-949">
          <div className="px-4 py-5 sm:px-6" data-id="element-950">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-951">
              Invoice Information
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-952">
              Please enter the basic invoice details
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-953">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-954">
              <div className="sm:col-span-3" data-id="element-955">
                <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700" data-id="element-956">
                  Invoice Number *
                </label>
                <div className="mt-1" data-id="element-957">
                  <input type="text" name="invoiceNumber" id="invoiceNumber" value={invoiceData.invoiceNumber} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.invoiceNumber ? 'border-red-300' : ''}`} data-id="element-958" />
                  {errors.invoiceNumber && <p className="mt-1 text-sm text-red-600" data-id="element-959">
                      {errors.invoiceNumber}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-960">
                <label htmlFor="poNumber" className="block text-sm font-medium text-gray-700" data-id="element-961">
                  Purchase Order Number
                </label>
                <div className="mt-1" data-id="element-962">
                  <input type="text" name="poNumber" id="poNumber" value={invoiceData.poNumber} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-963" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-964">
                <label htmlFor="invoiceDate" className="block text-sm font-medium text-gray-700" data-id="element-965">
                  Invoice Date *
                </label>
                <div className="mt-1" data-id="element-966">
                  <input type="date" name="invoiceDate" id="invoiceDate" value={invoiceData.invoiceDate} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.invoiceDate ? 'border-red-300' : ''}`} data-id="element-967" />
                  {errors.invoiceDate && <p className="mt-1 text-sm text-red-600" data-id="element-968">
                      {errors.invoiceDate}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-969">
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700" data-id="element-970">
                  Due Date
                </label>
                <div className="mt-1" data-id="element-971">
                  <input type="date" name="dueDate" id="dueDate" value={invoiceData.dueDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-972" />
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-973">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-974">
                  Description
                </label>
                <div className="mt-1" data-id="element-975">
                  <textarea id="description" name="description" rows={3} value={invoiceData.description} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md" data-id="element-976" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-977">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-978">
            <div data-id="element-979">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-980">
                Line Items
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-981">
                Add items included in this invoice
              </p>
            </div>
            <button type="button" onClick={addItem} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-982">
              Add Item
            </button>
          </div>
          <div className="border-t border-gray-200" data-id="element-983">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-984">
              <thead className="bg-gray-50" data-id="element-985">
                <tr data-id="element-986">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-987">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-988">
                    Quantity
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-989">
                    Unit Price
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-990">
                    Total
                  </th>
                  <th scope="col" className="relative px-6 py-3" data-id="element-991">
                    <span className="sr-only" data-id="element-992">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-993">
                {invoiceData.items.map((item, index) => <tr key={index} data-id="element-994">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-995">
                      <input type="text" value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} className={`block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${errors.items?.[index]?.description ? 'border-red-300' : ''}`} placeholder="Item description" data-id="element-996" />
                      {errors.items?.[index]?.description && <p className="mt-1 text-xs text-red-600" data-id="element-997">
                          {errors.items[index].description}
                        </p>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-998">
                      <input type="number" min="1" value={item.quantity} onChange={e => handleItemChange(index, 'quantity', e.target.value)} className={`block w-full shadow-sm sm:text-sm border-gray-300 rounded-md text-right ${errors.items?.[index]?.quantity ? 'border-red-300' : ''}`} data-id="element-999" />
                      {errors.items?.[index]?.quantity && <p className="mt-1 text-xs text-red-600" data-id="element-1000">
                          {errors.items[index].quantity}
                        </p>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1001">
                      <div className="relative rounded-md shadow-sm" data-id="element-1002">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1003">
                          <span className="text-gray-500 sm:text-sm" data-id="element-1004">$</span>
                        </div>
                        <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={e => handleItemChange(index, 'unitPrice', e.target.value)} className={`block w-full pl-7 pr-3 shadow-sm sm:text-sm border-gray-300 rounded-md text-right ${errors.items?.[index]?.unitPrice ? 'border-red-300' : ''}`} data-id="element-1005" />
                        {errors.items?.[index]?.unitPrice && <p className="mt-1 text-xs text-red-600" data-id="element-1006">
                            {errors.items[index].unitPrice}
                          </p>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-1007">
                      $
                      {((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1008">
                      <button type="button" onClick={() => removeItem(index)} disabled={invoiceData.items.length === 1} className={`text-red-600 hover:text-red-900 ${invoiceData.items.length === 1 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1009">
                        <XIcon className="h-5 w-5" data-id="element-1010" />
                      </button>
                    </td>
                  </tr>)}
                <tr className="bg-gray-50" data-id="element-1011">
                  <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-1012">
                    Total Amount
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-1013">
                    ${invoiceData.amount || '0.00'}
                  </td>
                  <td data-id="element-1014"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1015">
          <div className="px-4 py-5 sm:px-6" data-id="element-1016">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1017">
              Invoice Document
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-1018">
              Attach your invoice file (PDF, JPG, PNG)
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-1019">
            <div className="space-y-6" data-id="element-1020">
              <div data-id="element-1021">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1022">
                  Upload Invoice *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-1023">
                  <div className="space-y-1 text-center" data-id="element-1024">
                    {fileSelected ? <div className="flex items-center justify-center" data-id="element-1025">
                        <FileIcon className="h-10 w-10 text-indigo-500" data-id="element-1026" />
                        <span className="ml-2 text-sm text-gray-600" data-id="element-1027">
                          {fileSelected.name}
                        </span>
                        <button type="button" onClick={() => setFileSelected(null)} className="ml-2 text-red-600 hover:text-red-900" data-id="element-1028">
                          <XIcon className="h-4 w-4" data-id="element-1029" />
                        </button>
                      </div> : <>
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-1030" />
                        <div className="flex text-sm text-gray-600" data-id="element-1031">
                          <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-1032">
                            <span data-id="element-1033">Upload a file</span>
                            <input id="file-upload" name="file-upload" type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} data-id="element-1034" />
                          </label>
                          <p className="pl-1" data-id="element-1035">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500" data-id="element-1036">
                          PDF, JPG, PNG up to 10MB
                        </p>
                      </>}
                  </div>
                </div>
                {errors.file && <p className="mt-1 text-sm text-red-600" data-id="element-1037">{errors.file}</p>}
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end" data-id="element-1038">
          <button type="button" onClick={() => onSuccess()} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-3" data-id="element-1039">
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1040">
            {isSubmitting ? 'Submitting...' : 'Submit Invoice'}
          </button>
        </div>
        {Object.keys(errors).length > 0 && !errors.items && !errors.file && <div className="rounded-md bg-red-50 p-4" data-id="element-1041">
            <div className="flex" data-id="element-1042">
              <div className="flex-shrink-0" data-id="element-1043">
                <AlertCircleIcon className="h-5 w-5 text-red-400" data-id="element-1044" />
              </div>
              <div className="ml-3" data-id="element-1045">
                <h3 className="text-sm font-medium text-red-800" data-id="element-1046">
                  Please correct the errors above
                </h3>
              </div>
            </div>
          </div>}
      </form>
    </div>;
}